import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { adminAPI } from '../services/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [dashboardStats, setDashboardStats] = useState({
    totalApplications: 0,
    pendingApplications: 0,
    approvedApplications: 0,
    rejectedApplications: 0
  });
  const [pendingUsers, setPendingUsers] = useState([]);
  const [applicationsData, setApplicationsData] = useState([]);

  // Fetch dashboard data on mount
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Check if admin is logged in
      const adminToken = localStorage.getItem('adminToken');
      if (!adminToken) {
        navigate('/admin');
        return;
      }

      // Fetch all data
      const [statsData, usersData, appsData] = await Promise.all([
        adminAPI.getDashboardStats(),
        adminAPI.getPendingUsers(),
        adminAPI.getApplications()
      ]);

      if (statsData.stats) {
        setDashboardStats(statsData.stats);
      }
      
      setPendingUsers(usersData.users || []);
      setApplicationsData(appsData.applications || []);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
      
      // If unauthorized, redirect to login
      if (err.message?.includes('401')) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('admin');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async (user) => {
    try {
      const result = await adminAPI.sendReminder(user.id);
      if (result.success) {
        alert(`✅ Reminder email sent to ${user.name} (${user.email})`);
      } else {
        alert(`❌ Failed to send email: ${result.message}`);
      }
    } catch (err) {
      console.error('Error sending reminder:', err);
      alert('Failed to send reminder email. Please try again.');
    }
  };

  const handleViewApplication = (application) => {
    setSelectedApplication(application);
    setShowApplicationModal(true);
  };

  const handleUpdateStatus = async (applicationId, newStatus) => {
    try {
      const result = await adminAPI.updateApplicationStatus(applicationId, newStatus);
      if (result.success) {
        // Update local state
        setApplicationsData(prev => prev.map(app => 
          app.id === applicationId ? { ...app, status: newStatus } : app
        ));
        if (selectedApplication?.id === applicationId) {
          setSelectedApplication(prev => ({ ...prev, status: newStatus }));
        }
        // Refresh stats
        const statsData = await adminAPI.getDashboardStats();
        if (statsData.stats) {
          setDashboardStats(statsData.stats);
        }
        alert('✅ Application status updated successfully!');
      } else {
        alert(`❌ Failed to update status: ${result.message}`);
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update application status. Please try again.');
    }
  };

  const handleUpdateRemarks = async (applicationId, remarks) => {
    try {
      const result = await adminAPI.updateApplicationRemarks(applicationId, remarks);
      if (result.success) {
        setApplicationsData(prev => prev.map(app => 
          app.id === applicationId ? { ...app, remarks } : app
        ));
        if (selectedApplication?.id === applicationId) {
          setSelectedApplication(prev => ({ ...prev, remarks }));
        }
        alert('✅ Remarks updated successfully!');
      } else {
        alert(`❌ Failed to update remarks: ${result.message}`);
      }
    } catch (err) {
      console.error('Error updating remarks:', err);
      alert('Failed to update remarks. Please try again.');
    }
  };

  const handleToggleUniversityForm = async (applicationId, enabled) => {
    try {
      const result = await adminAPI.toggleUniversityForm(applicationId, enabled);
      if (result.success) {
        setApplicationsData(prev => prev.map(app => 
          app.id === applicationId ? { ...app, universityFormEnabled: enabled } : app
        ));
        if (selectedApplication?.id === applicationId) {
          setSelectedApplication(prev => ({ ...prev, universityFormEnabled: enabled }));
        }
        alert(`✅ University form ${enabled ? 'enabled' : 'disabled'} successfully!`);
      } else {
        alert(`❌ Failed to toggle university form: ${result.message}`);
      }
    } catch (err) {
      console.error('Error toggling university form:', err);
      alert('Failed to toggle university form. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'submitted': { label: 'Submitted', color: '#3b82f6' },
      'under-review': { label: 'Under Review', color: '#f59e0b' },
      'contacted': { label: 'Contacted', color: '#8b5cf6' },
      'accepted': { label: 'Accepted', color: '#10b981' },
      'rejected': { label: 'Rejected', color: '#ef4444' }
    };
    const config = statusConfig[status] || statusConfig['submitted'];
    return (
      <span className="status-badge" style={{ backgroundColor: `${config.color}20`, color: config.color, borderColor: `${config.color}50` }}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="admin-dashboard-page">
      <Header />
      
      <section className="admin-dashboard-section">
        <div className="container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <p>Loading dashboard data...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '50px', color: '#ef4444' }}>
              <p>{error}</p>
              <button onClick={fetchDashboardData} className="btn btn-primary" style={{ marginTop: '20px' }}>
                Retry
              </button>
            </div>
          ) : (
            <>
              <div className="dashboard-header">
                <div>
                  <h3 className="dashboard-subtitle">ADMINISTRATOR</h3>
                  <h1 className="dashboard-title">CONTROL <span className="title-highlight">DASHBOARD</span></h1>
                </div>
                <button className="btn-logout" onClick={() => {
                  adminAPI.logout();
                  navigate('/admin');
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="16 17 21 12 16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Logout
                </button>
              </div>

              <div className="dashboard-stats">
                <div className="stat-card">
                  <div className="stat-card-header">
                    <div className="stat-icon applications">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" stroke="currentColor" strokeWidth="2"/>
                        <rect x="8" y="2" width="8" height="4" rx="1" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                  </div>
                  <div className="stat-number">{dashboardStats.totalApplications}</div>
                  <div className="stat-label">Total Applications</div>
                </div>

                <div className="stat-card">
                  <div className="stat-card-header">
                    <div className="stat-icon pending">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        <polyline points="12 6 12 12 16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="stat-trend">{pendingUsers.length} Users</span>
                  </div>
                  <div className="stat-number">{dashboardStats.pendingApplications}</div>
                  <div className="stat-label">Pending Review</div>
                </div>

                <div className="stat-card">
                  <div className="stat-card-header">
                    <div className="stat-icon enrolled">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                        <polyline points="23 11 23 17 19 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="17 21 21 21 21 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <div className="stat-number">{dashboardStats.approvedApplications}</div>
                  <div className="stat-label">Approved</div>
                </div>

                <div className="stat-card">
                  <div className="stat-card-header">
                    <div className="stat-icon revenue">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <line x1="12" y1="1" x2="12" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <div className="stat-number">{dashboardStats.rejectedApplications}</div>
                  <div className="stat-label">Rejected</div>
                </div>
              </div>

              <div className="admin-tabs">
            <button 
              className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Overview
            </button>
            <button 
              className={`tab-btn ${activeTab === 'pending-users' ? 'active' : ''}`}
              onClick={() => setActiveTab('pending-users')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Pending Users
            </button>
            <button 
              className={`tab-btn ${activeTab === 'applications' ? 'active' : ''}`}
              onClick={() => setActiveTab('applications')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
                <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Applications
            </button>

              </div>

              <div className="tab-content">
            {activeTab === 'overview' && (
              <div className="overview-content">
                <div className="panel-card">
                  <div className="panel-header">
                    <h3>Quick Overview</h3>
                  </div>
                  <p className="overview-text">
                    Welcome to the Admin Dashboard. Use the tabs above to manage pending users, review applications, and configure settings.
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'pending-users' && (
              <div className="pending-users-content">
                <div className="panel-card">
                  <div className="panel-header">
                    <h3>Users Without Submitted Applications</h3>
                    <span className="count-badge">{pendingUsers.length} Users</span>
                  </div>
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Registered On</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingUsers.map(user => (
                          <tr key={user.id}>
                            <td>{user.name}</td>
                            <td>{user.email}</td>
                            <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                            <td>
                              <button 
                                className="btn-send-email"
                                onClick={() => handleSendEmail(user)}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2"/>
                                  <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                                Send Reminder
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'applications' && (
              <div className="applications-content">
                <div className="panel-card">
                  <div className="panel-header">
                    <h3>Received Applications</h3>
                    <span className="count-badge">{applicationsData.length} Applications</span>
                  </div>
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Candidate Name</th>
                          <th>Email</th>
                          <th>Education</th>
                          <th>Submitted On</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {applicationsData.map(app => (
                          <tr key={app.id}>
                            <td>{app.candidateName}</td>
                            <td>{app.email}</td>
                            <td>{app.educationLevel}</td>
                            <td>{new Date(app.submittedAt).toLocaleDateString()}</td>
                            <td>{getStatusBadge(app.status)}</td>
                            <td>
                              <button 
                                className="btn-view-details"
                                onClick={() => handleViewApplication(app)}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"/>
                                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
              </div>
            </>
          )}
        </div>
      </section>

      {showApplicationModal && selectedApplication && (
        <ApplicationModal 
          application={selectedApplication}
          onClose={() => setShowApplicationModal(false)}
          onUpdateStatus={handleUpdateStatus}
          onUpdateRemarks={handleUpdateRemarks}
          onToggleUniversityForm={handleToggleUniversityForm}
        />
      )}

      <Footer />
    </div>
  );
};

const ApplicationModal = ({ application, onClose, onUpdateStatus, onUpdateRemarks, onToggleUniversityForm }) => {
  const [remarks, setRemarks] = useState(application.remarks);
  const [status, setStatus] = useState(application.status);
  const [universityFormEnabled, setUniversityFormEnabled] = useState(application.universityFormEnabled);

  const handleSave = () => {
    onUpdateStatus(application.id, status);
    onUpdateRemarks(application.id, remarks);
    onToggleUniversityForm(application.id, universityFormEnabled);
    alert('Application updated successfully!');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Application Details</h2>
          <button className="btn-close-modal" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div className="application-details">
            <div className="detail-section">
              <h3>Personal Information</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Candidate Name:</label>
                  <span>{application.candidateName}</span>
                </div>
                <div className="detail-item">
                  <label>Email:</label>
                  <span>{application.email}</span>
                </div>
                <div className="detail-item">
                  <label>Age:</label>
                  <span>{application.age}</span>
                </div>
                <div className="detail-item">
                  <label>Gender:</label>
                  <span>{application.gender}</span>
                </div>
                <div className="detail-item full-width">
                  <label>Career Goals:</label>
                  <span>{application.careerGoals}</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3>Passport Details</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Passport Number:</label>
                  <span>{application.passportNumber}</span>
                </div>
                <div className="detail-item">
                  <label>Validity Date:</label>
                  <span>{new Date(application.passportValidity).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3>Education</h3>
              <div className="detail-item">
                <label>Education Level:</label>
                <span>{application.educationLevel}</span>
              </div>
            </div>

            <div className="detail-section">
              <h3>Uploaded Documents</h3>
              <div className="documents-list">
                {Object.entries(application.documents).map(([key, value]) => {
                  // Construct proper document URL
                  // Remove 'uploads/' prefix if it exists in the value, as we'll add the full URL
                  const filePath = value.startsWith('uploads/') ? value : `uploads/${value}`;
                  const documentUrl = `https://api-courses.aviator1903.com/${filePath}`;
                  
                  return (
                    <div key={key} className="document-item">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
                        <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      <span>{value}</span>
                      <button 
                        className="btn-view-doc"
                        onClick={() => window.open(documentUrl, '_blank')}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"/>
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        View
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="detail-section">
              <h3>Application Status</h3>
              <div className="form-group">
                <label htmlFor="status-select">Update Status:</label>
                <select 
                  id="status-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="submitted">Application Submitted</option>
                  <option value="under-review">Under Review</option>
                  <option value="contacted">Contacted by Counsellor</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            <div className="detail-section">
              <h3>University Form Access</h3>
              <div className="settings-item">
                <div className="setting-info">
                  <h4>Enable University Form Button</h4>
                  <p>Toggle to show/hide the university form button for this user</p>
                </div>
                <label className="toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={universityFormEnabled}
                    onChange={(e) => setUniversityFormEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className={`settings-status ${universityFormEnabled ? 'enabled' : 'disabled'}`}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  {universityFormEnabled ? (
                    <>
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="22 4 12 14.01 9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </>
                  ) : (
                    <>
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </>
                  )}
                </svg>
                University Form Button is <strong>{universityFormEnabled ? 'ENABLED' : 'DISABLED'}</strong> for this user
              </div>
            </div>

            <div className="detail-section">
              <h3>Admin Remarks</h3>
              <div className="form-group">
                <label htmlFor="remarks">Leave Remarks:</label>
                <textarea
                  id="remarks"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Add your remarks here..."
                  rows="4"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-save" onClick={handleSave}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" stroke="currentColor" strokeWidth="2"/>
              <polyline points="17 21 17 13 7 13 7 21" stroke="currentColor" strokeWidth="2"/>
              <polyline points="7 3 7 8 15 8" stroke="currentColor" strokeWidth="2"/>
            </svg>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;